var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, Card, CardContent, Checkbox, FormControlLabel, Link, TextField, Typography, } from "@mui/material";
import { useEffect } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAddExistingPlayersToTeam, useRetrieveAddablePlayers, useRetrieveTeam, useRetrieveUserTeamPermissions, } from "../ApiProvider";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { useNumericParams } from "../utils/useNumericParams";
import { playerDisplayName } from "./PlayerDisplayNameLink";
import { countryCodeToEmoji } from "./countries";
export var AddExistingPlayersToTeam = function () {
    var teamId = useNumericParams().id;
    var navigate = useNavigate();
    var _a = useRetrieveUserTeamPermissions({ teamId: teamId }), userTeamPermissions = _a.data, userTeamPermissionsIsLoading = _a.isLoading, userTeamPermissionsError = _a.error;
    var _b = (userTeamPermissions || {}).canModify, canModify = _b === void 0 ? false : _b;
    var _c = useRetrieveTeam({ id: teamId, enabled: canModify }), team = _c.data, teamIsLoading = _c.isLoading, teamError = _c.error;
    var _d = useRetrieveAddablePlayers({
        teamId: teamId,
        enabled: canModify,
    }), _e = _d.data, addablePlayersData = _e === void 0 ? [] : _e, addablePlayersIsLoading = _d.isLoading, addablePlayersError = _d.error;
    var _f = useForm({
        defaultValues: { players: [] },
    }), control = _f.control, setValue = _f.setValue, handleSubmit = _f.handleSubmit;
    var fields = useFieldArray({ control: control, name: "players" }).fields;
    var watchedPlayers = useWatch({ control: control, name: "players" });
    useEffect(function () {
        if (addablePlayersData.length > 0 && fields.length === 0) {
            var sorted = __spreadArray([], __read(addablePlayersData), false).sort(function (a, b) {
                return playerDisplayName(a).localeCompare(playerDisplayName(b));
            });
            setValue("players", sorted.map(function (p) {
                var _a;
                return ({
                    playerId: p.id,
                    selected: false,
                    squadNumber: null,
                    displayName: playerDisplayName(p),
                    nationality: (_a = p.nationality) !== null && _a !== void 0 ? _a : null,
                });
            }));
        }
    }, [addablePlayersData, fields.length, setValue]);
    var addPlayersMutation = useAddExistingPlayersToTeam({
        teamId: teamId,
        onMutate: function () { },
        onSuccess: function () {
            navigate("/teams/".concat(teamId, "/"));
        },
        onError: function () { },
    });
    if (userTeamPermissionsIsLoading ||
        teamIsLoading ||
        addablePlayersIsLoading) {
        return _jsx(Loading, {});
    }
    if (userTeamPermissionsError) {
        return _jsx(ErrorView, { error: userTeamPermissionsError });
    }
    if (teamError) {
        return _jsx(ErrorView, { error: teamError });
    }
    if (addablePlayersError) {
        return _jsx(ErrorView, { error: addablePlayersError });
    }
    if (!canModify) {
        return _jsx(Forbidden, {});
    }
    var selectedCount = (watchedPlayers || []).filter(function (p) { return p.selected; }).length;
    var handleSelectAll = function () {
        var allSelected = selectedCount === fields.length;
        fields.forEach(function (_, i) {
            setValue("players.".concat(i, ".selected"), !allSelected);
        });
    };
    var onSubmit = function (data) {
        var selected = data.players.filter(function (p) { return p.selected; });
        if (selected.length === 0)
            return;
        var players = selected.map(function (p) { return (__assign({ playerId: p.playerId }, (p.squadNumber != null ? { squadNumber: p.squadNumber } : {}))); });
        addPlayersMutation.mutate({ players: players });
    };
    return (_jsxs(Box, { component: "form", onSubmit: handleSubmit(onSubmit), noValidate: true, sx: { maxWidth: 800, mx: "auto", p: 3, mb: 6 }, children: [_jsxs(Typography, { variant: "h4", sx: { textAlign: "center", mb: 1, fontWeight: 600 }, children: ["Add Players to", " ", _jsx(Link, { component: RouterLink, to: "/teams/".concat(teamId, "/"), underline: "hover", children: team.name })] }), _jsx(Typography, { variant: "body1", color: "text.secondary", sx: { textAlign: "center", mb: 4 }, children: "Select the players you want to add to this team" }), fields.length === 0 ? (_jsxs(Card, { sx: { p: 4, textAlign: "center" }, children: [_jsx(Typography, { variant: "h6", sx: { mb: 2, color: "text.secondary" }, children: "No Players Available" }), _jsx(Typography, { variant: "body1", color: "text.secondary", sx: { mb: 3 }, children: "You don't have any players available to add to this team. All your players are either already in this team or you haven't created any players yet." }), _jsx(Button, { component: RouterLink, to: "/teams/".concat(teamId, "/add-player/create"), variant: "contained", sx: { mr: 2 }, children: "Create New Player" }), _jsx(Button, { component: RouterLink, to: "/teams/".concat(teamId, "/add-player/"), variant: "outlined", children: "Back" })] })) : (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: {
                            mb: 3,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }, children: [_jsxs(Typography, { variant: "h6", children: [selectedCount, " of ", fields.length, " players selected"] }), _jsx(Button, { onClick: handleSelectAll, variant: "outlined", size: "small", children: selectedCount === fields.length ? "Deselect All" : "Select All" })] }), _jsx(Box, { sx: { mb: 4 }, children: fields.map(function (field, index) {
                            var _a, _b;
                            var isSelected = (_b = (_a = watchedPlayers === null || watchedPlayers === void 0 ? void 0 : watchedPlayers[index]) === null || _a === void 0 ? void 0 : _a.selected) !== null && _b !== void 0 ? _b : false;
                            return (_jsx(Card, { sx: {
                                    mb: 2,
                                    border: isSelected ? "2px solid" : "1px solid",
                                    borderColor: isSelected ? "primary.main" : "grey.300",
                                    bgcolor: isSelected ? "primary.50" : "background.paper",
                                    cursor: "pointer",
                                }, onClick: function () {
                                    return setValue("players.".concat(index, ".selected"), !isSelected);
                                }, children: _jsxs(CardContent, { sx: {
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        py: 2,
                                    }, children: [_jsx(FormControlLabel, { control: _jsx(Controller, { name: "players.".concat(index, ".selected"), control: control, render: function (_a) {
                                                    var checkboxField = _a.field;
                                                    return (_jsx(Checkbox, { checked: checkboxField.value, onChange: function (e) {
                                                            return checkboxField.onChange(e.target.checked);
                                                        }, onClick: function (e) { return e.stopPropagation(); } }));
                                                } }), label: "", sx: { m: 0 }, onClick: function (e) { return e.stopPropagation(); } }), _jsx(Box, { sx: { flex: 1 }, children: _jsxs(Typography, { variant: "h6", children: [field.nationality &&
                                                        "".concat(countryCodeToEmoji(field.nationality), " "), field.displayName] }) }), isSelected && (_jsx(Controller, { name: "players.".concat(index, ".squadNumber"), control: control, render: function (_a) {
                                                var _b;
                                                var sqField = _a.field;
                                                return (_jsx(TextField, { value: (_b = sqField.value) !== null && _b !== void 0 ? _b : "", onChange: function (e) {
                                                        var val = e.target.value;
                                                        sqField.onChange(val === "" ? null : parseInt(val, 10));
                                                    }, label: "#", type: "number", size: "small", onClick: function (e) { return e.stopPropagation(); }, inputProps: { min: 1, max: 99 }, sx: { width: 80 }, placeholder: "1-99" }));
                                            } }))] }) }, field.id));
                        }) }), _jsxs(Box, { sx: { display: "flex", justifyContent: "center", gap: 2 }, children: [_jsx(Button, { type: "submit", disabled: selectedCount === 0 || addPlayersMutation.isLoading, variant: "contained", size: "large", sx: { px: 4 }, children: addPlayersMutation.isLoading
                                    ? "Adding Players..."
                                    : "Add ".concat(selectedCount, " Player").concat(selectedCount !== 1 ? "s" : "") }), _jsx(Button, { component: RouterLink, to: "/teams/".concat(teamId, "/add-player/"), variant: "outlined", size: "large", disabled: addPlayersMutation.isLoading, children: "Back" })] })] }))] }));
};
